import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import {
  FiSearch, FiUser, FiHeart, FiShoppingBag, FiMenu, FiX,
  FiChevronDown, FiChevronRight, FiGift, FiPhone, FiTruck,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import API from '../utils/api';
import { trackSearch } from '../utils/metaPixel';
import { getCategoryImage, handleImageError } from '../utils/imageHelper';
import { AGE_GROUPS } from '../data/ageGroups';
import { AgeCard } from './ShopByAge';

const NO_IMG = 'default-category.jpg';

const MENU = [
  { label: 'All Toys',      to: '/products' },
  { label: 'Shop by Age',   menu: 'age' },
  { label: 'Categories',    menu: 'cat' },
  { label: 'Bestsellers',   to: '/products?sort=popular' },
  { label: 'New Arrivals',  to: '/products?sort=newest' },
  { label: 'Gifts',         to: '/gift-guide' },
  { label: 'Toy Guides',    to: '/blogs' },
];

const ACCOUNT_LINKS = [
  { to: '/profile',  label: 'My Profile' },
  { to: '/orders',   label: 'My Orders' },
  { to: '/wishlist', label: 'My Wishlist' },
  { to: '/wallet',   label: 'Wallet & Rewards' },
];

const IconButton = ({ children, label, badge, ...props }) => (
  <button type="button" aria-label={label}
    className="relative flex flex-col items-center gap-0.5 px-2 py-1 text-espresso hover:text-terra transition-colors" {...props}>
    {children}
    <span className="hidden xl:block font-sans text-[10px] font-medium">{label}</span>
    {badge > 0 && (
      <span className="absolute -top-0.5 right-0.5 xl:right-1.5 min-w-[17px] h-[17px] px-1 rounded-full bg-terra-500 text-white text-[10px] font-bold flex items-center justify-center">
        {badge}
      </span>
    )}
  </button>
);

const SearchForm = ({ value, onChange, onSubmit, className = '' }) => (
  <form onSubmit={onSubmit} role="search" className={`relative ${className}`}>
    <label htmlFor="site-search" className="sr-only">Search toys</label>
    <input id="site-search" type="search" value={value} onChange={e => onChange(e.target.value)}
      placeholder="Search for toys, puzzles, age…"
      className="w-full h-11 pl-4 pr-12 rounded-full bg-skin-50 border border-skin-100 font-sans text-sm text-espresso placeholder-wood-300 focus:outline-none focus:bg-white focus:border-espresso transition" />
    <button type="submit" aria-label="Search"
      className="absolute right-1 top-1 w-9 h-9 rounded-full bg-espresso text-white flex items-center justify-center hover:bg-terra transition">
      <FiSearch className="text-[15px]" />
    </button>
  </form>
);

const Navbar = ({ onCartOpen }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Drawer links duplicate the desktop menu; build them on first open to keep the page light
  const [drawerReady, setDrawerReady] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'age' | 'cat' | 'user'
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const cartCount = getCartCount();

  useEffect(() => {
    API.get('/categories').then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  // Close menus whenever the route changes
  useEffect(() => { setDrawerOpen(false); setOpenMenu(null); }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    trackSearch(q);
    navigate(`/products?search=${encodeURIComponent(q)}`);
    setSearchQuery('');
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const hoverProps = (key) => ({
    onMouseEnter: () => setOpenMenu(key),
    onMouseLeave: () => setOpenMenu(null),
  });

  return (
    <>
      {/* ── Offer bar ── */}
      <div className="bg-espresso text-skin-100">
        <div className="container-custom flex items-center justify-center lg:justify-between h-8 font-sans text-[11px] md:text-xs">
          <p className="flex items-center gap-2">
            <FiTruck className="text-gold-300" /> Free shipping above ₹499 · COD available · 7-day easy returns
          </p>
          <div className="hidden lg:flex items-center gap-5 text-skin-200">
            <a href="tel:+918807259471" className="flex items-center gap-1.5 hover:text-white"><FiPhone /> +91 88072 59471</a>
            <a href="https://wa.me/918807259471" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-white"><FaWhatsapp /> WhatsApp</a>
            <Link to="/refer-friend" className="flex items-center gap-1.5 text-gold-200 hover:text-white"><FiGift /> Refer &amp; Earn</Link>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-white border-b border-skin-100">
        {/* ── Main row ── */}
        <div className="container-custom flex items-center gap-3 lg:gap-8 h-16 lg:h-[72px]">
          <button type="button" className="lg:hidden -ml-1 p-2 text-espresso" aria-label="Open menu" onClick={() => { setDrawerReady(true); setDrawerOpen(true); }}>
            <FiMenu className="text-[22px]" />
          </button>

          <Link to="/" className="flex-shrink-0">
            <BrandLogo />
          </Link>

          <SearchForm value={searchQuery} onChange={setSearchQuery} onSubmit={handleSearch} className="hidden lg:block flex-1 max-w-xl" />

          <div className="ml-auto flex items-center">
            {/* Account */}
            <div className="relative hidden lg:block" {...hoverProps('user')}>
              <IconButton label={isAuthenticated ? (user?.name?.split(' ')[0] || 'Account') : 'Sign in'}
                onClick={() => !isAuthenticated && navigate('/login')}>
                <FiUser className="text-[19px]" />
              </IconButton>
              <div className={`absolute right-0 top-full pt-2 w-60 transition ${openMenu === 'user' ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                <div className="bg-white rounded-xl border border-skin-100 shadow-strong overflow-hidden">
                  {isAuthenticated ? (
                    <>
                      <div className="px-4 py-3 border-b border-skin-100">
                        <p className="font-sans font-semibold text-sm text-espresso truncate">{user?.name}</p>
                        <p className="font-sans text-xs text-wood-400 truncate">{user?.email || user?.phone}</p>
                      </div>
                      <div className="py-1">
                        {ACCOUNT_LINKS.map(l => (
                          <Link key={l.to} to={l.to} className="block px-4 py-2 font-sans text-sm text-espresso hover:bg-skin-50">{l.label}</Link>
                        ))}
                        {isAdmin && <Link to="/admin" className="block px-4 py-2 font-sans text-sm text-espresso hover:bg-skin-50">Admin Dashboard</Link>}
                      </div>
                      <button type="button" onClick={handleLogout}
                        className="w-full text-left px-4 py-2.5 border-t border-skin-100 font-sans text-sm text-terra hover:bg-terra-50">
                        Log out
                      </button>
                    </>
                  ) : (
                    <div className="p-4">
                      <p className="font-sans font-semibold text-sm text-espresso">Welcome to Thrayam Toys</p>
                      <p className="font-sans text-xs text-wood-400 mt-0.5 mb-3">Track orders, save favourites and earn rewards.</p>
                      <Link to="/login" className="block text-center py-2.5 rounded-full bg-espresso text-white font-sans font-semibold text-sm hover:bg-wood-600">
                        Sign in / Register
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <span className="hidden sm:block">
              <IconButton label="Wishlist" onClick={() => navigate(isAuthenticated ? '/wishlist' : '/login')}>
                <FiHeart className="text-[19px]" />
              </IconButton>
            </span>

            <IconButton label="Cart" badge={cartCount} onClick={onCartOpen}>
              <FiShoppingBag className="text-[19px]" />
            </IconButton>
          </div>
        </div>

        {/* ── Mobile search ── */}
        <div className="lg:hidden container-custom pb-3">
          <SearchForm value={searchQuery} onChange={setSearchQuery} onSubmit={handleSearch} />
        </div>

        {/* ── Desktop menu row ── */}
        <nav aria-label="Main" className="hidden lg:block border-t border-skin-100">
          <ul className="container-custom flex items-center gap-1 h-11">
            {MENU.map(item => (
              <li key={item.label} className={item.menu ? '' : 'relative'} {...(item.menu ? hoverProps(item.menu) : {})}>
                {item.menu ? (
                  <button type="button" aria-expanded={openMenu === item.menu}
                    className={`flex items-center gap-1 px-3 h-11 font-sans text-[13px] font-semibold transition-colors ${openMenu === item.menu ? 'text-terra' : 'text-espresso hover:text-terra'}`}>
                    {item.label} <FiChevronDown className={`text-xs transition-transform ${openMenu === item.menu ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <NavLink to={item.to}
                    className="flex items-center px-3 h-11 font-sans text-[13px] font-semibold text-espresso hover:text-terra transition-colors">
                    {item.label}
                  </NavLink>
                )}

                {/* Mega menu: Shop by Age */}
                {item.menu === 'age' && (
                  <div className={`absolute left-0 right-0 top-full bg-white border-y border-skin-100 shadow-strong transition ${openMenu === 'age' ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                    <div className="container-custom py-6">
                      <ul className="grid grid-cols-8 gap-3">
                        {AGE_GROUPS.map(age => <li key={age.slug}><AgeCard age={age} compact /></li>)}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Mega menu: Categories */}
                {item.menu === 'cat' && (
                  <div className={`absolute left-0 right-0 top-full bg-white border-y border-skin-100 shadow-strong transition ${openMenu === 'cat' ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                    <div className="container-custom py-6 grid grid-cols-4 xl:grid-cols-5 gap-2">
                      {categories.map(cat => (
                        <Link key={cat._id} to={`/products?category=${cat._id}`}
                          className="flex items-center gap-3 p-2 rounded-xl hover:bg-skin-50 transition">
                          <span className="w-11 h-11 rounded-lg overflow-hidden bg-skin-100 flex-shrink-0 flex items-center justify-center">
                            {cat.image && cat.image !== NO_IMG
                              ? <img src={getCategoryImage(cat)} alt={`${cat.name} toys`} loading="lazy" onError={e => handleImageError(e, 'category')} className="w-full h-full object-cover" />
                              : <span className="font-sans font-bold text-wood-500">{cat.name?.charAt(0)}</span>}
                          </span>
                          <span className="font-sans text-sm font-medium text-espresso">{cat.name}</span>
                        </Link>
                      ))}
                      <Link to="/products" className="flex items-center gap-2 p-2 font-sans text-sm font-semibold text-terra">
                        View all toys <FiChevronRight />
                      </Link>
                    </div>
                  </div>
                )}
              </li>
            ))}
            <li className="ml-auto">
              <NavLink to="/gift-cards" className="px-3 font-sans text-[13px] font-medium text-wood-400 hover:text-espresso">Gift Cards</NavLink>
            </li>
            <li>
              <NavLink to="/bulk-orders" className="px-3 font-sans text-[13px] font-medium text-wood-400 hover:text-espresso">Bulk Orders</NavLink>
            </li>
            <li>
              <NavLink to="/about" className="px-3 font-sans text-[13px] font-medium text-wood-400 hover:text-espresso">About</NavLink>
            </li>
            <li>
              <NavLink to="/contact" className="px-3 font-sans text-[13px] font-medium text-wood-400 hover:text-espresso">Contact</NavLink>
            </li>
          </ul>
        </nav>
      </header>

      {/* ── Mobile drawer ── */}
      <div className={`lg:hidden fixed inset-0 z-[60] transition ${drawerOpen ? 'visible' : 'invisible'}`} aria-hidden={!drawerOpen}>
        <div className={`absolute inset-0 bg-black/40 transition-opacity ${drawerOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setDrawerOpen(false)} />
        <aside className={`absolute left-0 top-0 bottom-0 w-[86%] max-w-sm bg-white flex flex-col transition-transform duration-300 ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
          aria-label="Menu">
          <div className="flex items-center justify-between px-4 h-14 border-b border-skin-100">
            {isAuthenticated
              ? <span className="font-sans font-semibold text-sm text-espresso truncate">Hi, {user?.name?.split(' ')[0]}</span>
              : <Link to="/login" className="font-sans font-semibold text-sm text-espresso inline-flex items-center gap-2"><FiUser /> Sign in / Register</Link>}
            <button type="button" aria-label="Close menu" className="p-2 text-espresso" onClick={() => setDrawerOpen(false)}>
              <FiX className="text-xl" />
            </button>
          </div>

          {drawerReady && <div className="flex-1 overflow-y-auto">
            <div className="px-4 pt-4">
              <p className="font-sans text-[11px] font-semibold uppercase tracking-wider text-wood-400 mb-2">Shop by Age</p>
              <ul className="grid grid-cols-4 gap-2">
                {AGE_GROUPS.map(age => <li key={age.slug}><AgeCard age={age} compact /></li>)}
              </ul>
            </div>

            <ul className="mt-4 border-t border-skin-100">
              {MENU.filter(m => m.to).map(m => (
                <li key={m.label}>
                  <Link to={m.to} className="flex items-center justify-between px-4 py-3 border-b border-skin-100 font-sans text-sm font-semibold text-espresso">
                    {m.label} <FiChevronRight className="text-wood-300" />
                  </Link>
                </li>
              ))}
            </ul>

            {categories.length > 0 && (
              <div className="px-4 pt-4">
                <p className="font-sans text-[11px] font-semibold uppercase tracking-wider text-wood-400 mb-1">Categories</p>
                <ul>
                  {categories.map(cat => (
                    <li key={cat._id}>
                      <Link to={`/products?category=${cat._id}`} className="block py-2.5 font-sans text-sm text-espresso">{cat.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="px-4 py-4 mt-2 border-t border-skin-100 space-y-1">
              {isAuthenticated && ACCOUNT_LINKS.map(l => (
                <Link key={l.to} to={l.to} className="block py-2 font-sans text-sm text-espresso">{l.label}</Link>
              ))}
              {isAdmin && <Link to="/admin" className="block py-2 font-sans text-sm text-espresso">Admin Dashboard</Link>}
              <Link to="/refer-friend" className="flex items-center gap-2 py-2 font-sans text-sm font-semibold text-terra"><FiGift /> Refer &amp; Earn</Link>
              <Link to="/gift-cards" className="block py-2 font-sans text-sm text-espresso">Gift cards</Link>
              <Link to="/bulk-orders" className="block py-2 font-sans text-sm text-espresso">Bulk &amp; corporate orders</Link>
              <Link to="/about" className="block py-2 font-sans text-sm text-espresso">About us</Link>
              <Link to="/contact" className="block py-2 font-sans text-sm text-espresso">Contact</Link>
              {isAuthenticated && (
                <button type="button" onClick={handleLogout} className="block py-2 font-sans text-sm text-terra">Log out</button>
              )}
            </div>
          </div>}

          <div className="mt-auto px-4 py-3 border-t border-skin-100 flex items-center gap-4 font-sans text-xs text-wood-400">
            <a href="tel:+918807259471" className="flex items-center gap-1.5"><FiPhone /> +91 88072 59471</a>
            <a href="https://wa.me/918807259471" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5"><FaWhatsapp /> WhatsApp</a>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Navbar;
